import React from 'react';
import ClientEvents from './pages/cliente/Events';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import WelcomeModal from './components/tour/WelcomeModal';
import CompletionModal from './components/tour/CompletionModal';
import HelpButton from './components/tour/HelpButton';
import QuickGuide from './components/tour/QuickGuide';
import TourProgressBar from './components/tour/TourProgressBar';

// Pages
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';
import Layout from './components/Layout';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import UsersPage from './pages/admin/Users';
import UserAdminProfile from './pages/admin/UserAdminProfile';
import UserForm from './pages/admin/UserForm';
import OrgChart from './pages/admin/OrgChart';
import ClientsPage from './pages/admin/Clients';
import AuditLogPage from './pages/admin/AuditLog';
import MirrorModePage from './pages/admin/MirrorMode';
import TemplatesList from './pages/admin/templates/TemplatesList';
import TemplateBuilder from './pages/admin/templates/TemplateBuilder';
import AdminSettings from './pages/admin/AdminSettings';
import FAQAdmin from './pages/admin/FAQAdmin';
import ArchivoPage from './pages/admin/Archivo';
import EvaluationsListPage from './pages/admin/EvaluationsList';
import FAQ from './pages/FAQ';
import Wiki from './pages/Wiki';

// Location pages
import LocationsList from './pages/locations/LocationsList';
import LocationDetail from './pages/locations/LocationDetail';
import LocationForm from './pages/locations/LocationForm';

// Event pages
import EventsList from './pages/events/EventsList';
import Providers from './components/traceability/Providers';
import EventDetail from './pages/events/EventDetail';
import EventForm from './pages/events/EventForm';

// Emergency & Authority pages
import EmergencyServicesList from './pages/emergency/EmergencyList';
import EmergencyServiceDetail from './pages/emergency/EmergencyDetail';
import EmergencyServiceForm from './pages/emergency/EmergencyForm';
import AuthoritiesList from './pages/authorities/AuthoritiesList';
import AuthorityDetail from './pages/authorities/AuthorityDetail';
import AuthorityForm from './pages/authorities/AuthorityForm';

// Staff & Equipment pages
import StaffDirectory from './pages/staff/StaffDirectory';
import StaffForm from './pages/staff/StaffForm';
import EquipmentCatalog from './pages/admin/EquipmentCatalog';
import PublicRegistration from './pages/public/PublicRegistration';

// Lider pages
import LiderDashboard from './pages/lider/Dashboard';
import ProjectsPage from './pages/lider/Projects';
import ProjectDetailPage from './pages/lider/ProjectDetail';
import EvaluationReviewPage from './pages/lider/EvaluationReview';

import EvaluationCompare from './pages/lider/EvaluationCompare';
import LevantadorDashboard from './pages/levantador/Dashboard';
import EvaluationChecklist from './pages/levantador/EvaluationChecklist';
import EvaluationMap from './pages/levantador/EvaluationMap';

// Cliente pages
import ClienteDashboard from './pages/cliente/Dashboard';
import ClienteEvaluationDetail from './pages/cliente/EvaluationDetail';
import ClienteMapPage from './pages/cliente/Map';
import ClienteIncidentsPage from './pages/cliente/Incidents';

// Shared pages
import UserProfile from './pages/UserProfile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-screen bg-soem-bg">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-soem-primary border-t-transparent" />
    </div>
  );
}

function RoleRoute({ children, roles }) {
  const { isAuthenticated, activeRole, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(activeRole)) return <Navigate to="/access-denied" replace />;
  return children;
}

/** Resolve the default landing route for a given role. */
function getDefaultRouteForRole(role) {
  if (role === 'cliente') return '/cliente';
  return '/dashboard';
}

function SmartRedirect() {
  const { isAuthenticated, activeRole, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRouteForRole(activeRole)} replace />;
}

function LoginRedirect() {
  const { isAuthenticated, activeRole, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (isAuthenticated) return <Navigate to={getDefaultRouteForRole(activeRole)} replace />;
  return <Login />;
}

function DashboardRouter() {
  const { activeRole } = useAuth();
  if (activeRole === 'super_admin') return <AdminDashboard />;
  if (activeRole === 'lider_proyecto') return <LiderDashboard />;
  if (activeRole === 'levantador') return <LevantadorDashboard />;
  return <Navigate to="/access-denied" replace />;
}

const MGMT_ROLES = ['super_admin', 'lider_proyecto', 'levantador'];

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SmartRedirect />} />
          <Route path="/login" element={<LoginRedirect />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/perfil" element={<RoleRoute roles={[...MGMT_ROLES, 'cliente']}><UserProfile /></RoleRoute>} />
          {/* PUBLIC — no auth required */}
          <Route path="/registro/:eventId" element={<PublicRegistration />} />

          {/* Management routes */}
          <Route
            path="/dashboard"
            element={
              <RoleRoute roles={MGMT_ROLES}>
                <Layout />
              </RoleRoute>
            }
          >
            <Route index element={<DashboardRouter />} />
            <Route path="usuarios" element={<RoleRoute roles={['super_admin']}><UsersPage /></RoleRoute>} />
            <Route path="usuarios/nuevo" element={<RoleRoute roles={['super_admin']}><UserForm /></RoleRoute>} />
            <Route path="usuarios/organigrama" element={<RoleRoute roles={['super_admin']}><OrgChart /></RoleRoute>} />
            <Route path="usuarios/:id" element={<RoleRoute roles={['super_admin']}><UserAdminProfile /></RoleRoute>} />
            <Route path="usuarios/:id/editar" element={<RoleRoute roles={['super_admin']}><UserForm /></RoleRoute>} />
            <Route
              path="clientes"
              element={
                <RoleRoute roles={['super_admin', 'lider_proyecto']}>
                  <ClientsPage />
                </RoleRoute>
              }
            />
            <Route
              path="auditoria"
              element={
                <RoleRoute roles={['super_admin']}>
                  <AuditLogPage />
                </RoleRoute>
              }
            />
            <Route
              path="modo-espejo"
              element={
                <RoleRoute roles={['super_admin']}>
                  <MirrorModePage />
                </RoleRoute>
              }
            />
            <Route path="proyectos" element={<ProjectsPage />} />
            <Route path="proyectos/:id" element={<ProjectDetailPage />} />
            <Route path="proyectos/:id/comparar" element={<EvaluationCompare />} />
            {/* #EvalsGlobalList — lista global de evaluaciones en Operaciones. */}
            <Route path="evaluaciones" element={<RoleRoute roles={['super_admin','lider_proyecto']}><EvaluationsListPage /></RoleRoute>} />
            <Route path="evaluaciones/:id/revision" element={<EvaluationReviewPage />} />
            <Route path="evaluaciones/:id/checklist" element={<EvaluationChecklist />} />
            <Route path="templates" element={
              <RoleRoute roles={['super_admin', 'lider_proyecto']}><TemplatesList /></RoleRoute>
            } />
            <Route path="locations" element={<LocationsList />} />
            <Route path="locations/new" element={<RoleRoute roles={['super_admin','lider_proyecto']}><LocationForm /></RoleRoute>} />
            <Route path="locations/:id" element={<LocationDetail />} />
            <Route path="locations/:id/edit" element={<RoleRoute roles={['super_admin','lider_proyecto']}><LocationForm /></RoleRoute>} />
            <Route path="events" element={<EventsList />} />
            <Route path="events/new" element={<RoleRoute roles={['super_admin','lider_proyecto']}><EventForm /></RoleRoute>} />
            <Route path="events/:id" element={<EventDetail />} />
            <Route path="events/:id/edit" element={<RoleRoute roles={['super_admin','lider_proyecto']}><EventForm /></RoleRoute>} />
            <Route path="providers" element={
              <RoleRoute roles={['super_admin', 'lider_proyecto', 'levantador']}><Providers /></RoleRoute>
            } />
            <Route path="emergency-services" element={<EmergencyServicesList />} />
            <Route path="emergency-services/new" element={<RoleRoute roles={['super_admin','lider_proyecto','levantador']}><EmergencyServiceForm /></RoleRoute>} />
            <Route path="emergency-services/:id" element={<EmergencyServiceDetail />} />
            <Route path="emergency-services/:id/edit" element={<RoleRoute roles={['super_admin','lider_proyecto','levantador']}><EmergencyServiceForm /></RoleRoute>} />
            <Route path="authorities" element={<AuthoritiesList />} />
            <Route path="authorities/new" element={<RoleRoute roles={['super_admin','lider_proyecto','levantador']}><AuthorityForm /></RoleRoute>} />
            <Route path="authorities/:id" element={<AuthorityDetail />} />
            <Route path="authorities/:id/edit" element={<RoleRoute roles={['super_admin','lider_proyecto','levantador']}><AuthorityForm /></RoleRoute>} />
            <Route path="staff" element={<StaffDirectory />} />
            <Route path="staff/nuevo" element={<RoleRoute roles={['super_admin','lider_proyecto']}><StaffForm /></RoleRoute>} />
            <Route path="staff/:id/editar" element={<RoleRoute roles={['super_admin','lider_proyecto']}><StaffForm /></RoleRoute>} />
            <Route path="equipment-catalog" element={<RoleRoute roles={['super_admin']}><EquipmentCatalog /></RoleRoute>} />
            <Route path="configuracion" element={<RoleRoute roles={['super_admin']}><AdminSettings /></RoleRoute>} />
            <Route path="faq" element={<RoleRoute roles={['super_admin']}><FAQAdmin /></RoleRoute>} />
            <Route path="archivo" element={<RoleRoute roles={['super_admin']}><ArchivoPage /></RoleRoute>} />
            <Route path="ayuda" element={<FAQ />} />
            <Route path="wiki" element={<Wiki />} />
          </Route>

          {/* Template Builder — fullscreen, outside layout */}
          <Route path="/dashboard/templates/:id/edit"
            element={<RoleRoute roles={['super_admin', 'lider_proyecto']}><TemplateBuilder /></RoleRoute>} />

          {/* Evaluation Map — fullscreen, outside layout */}
          <Route
            path="/dashboard/evaluaciones/:id/mapa"
            element={
              <RoleRoute roles={MGMT_ROLES}>
                <EvaluationMap />
              </RoleRoute>
            }
          />

          {/* Cliente microsite */}
          <Route
            path="/cliente"
            element={
              <RoleRoute roles={['cliente']}>
                <Layout isCliente />
              </RoleRoute>
            }
          >
            <Route index element={<ClienteDashboard />} />
            <Route path="evaluaciones/:id" element={<ClienteEvaluationDetail />} />
            <Route path="mapa" element={<ClienteMapPage />} />
            <Route path="incidentes" element={<ClienteIncidentsPage />} />
            <Route path="eventos" element={<ClientEvents />} />
            <Route path="eventos/:id" element={<ClientEvents />} />
            <Route path="ayuda" element={<FAQ />} />
            <Route path="wiki" element={<Wiki />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        {/* Tour components — only shown when authenticated */}
        <TourOverlay />
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

function TourOverlay() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  return (
    <OnboardingProvider>
      <WelcomeModal />
      <CompletionModal />
      <HelpButton />
      <QuickGuide />
      <TourProgressBar />
    </OnboardingProvider>
  );
}
